import { useEffect, useMemo, useState } from "react";

/**
 * Animated multi-stage loader for long-running scans / AI calls.
 *
 * Props:
 *   stages: string[]   — list of stage labels (in order)
 *   logLines: string[] — optional pool of fake "live log" lines that scroll by;
 *                        component picks a few at random to feel like real
 *                        terminal output. Pass [] to disable the log strip.
 *   stageDurationMs: number — how long each stage stays "current" before
 *                             marking it done. Default 4500ms.
 *   subtitle: string  — small text shown below the cursor (e.g. ETA)
 */
export default function ScanLoader({
  stages = [],
  logLines = [],
  stageDurationMs = 4500,
  subtitle = "",
  /**
   * When provided, the active stage marker is driven by this value instead
   * of auto-advancing on a timer — keeps the UI honest with the backend's
   * real progress. Pass either:
   *   - a 0-based index into `stages`, OR
   *   - a string key that will be matched against `stageKeys[i]`
   * The `stageKeys` array, when given, maps each visible stage label to
   * the backend's stage identifier (e.g. "probing_audio").
   */
  currentStage = null,
  stageKeys = null,
}) {
  const [autoIdx, setAutoIdx] = useState(0);

  // Auto-advance only when no backend stage is driving the indicator.
  useEffect(() => {
    if (stages.length === 0 || currentStage != null) return;
    const id = setInterval(() => {
      setAutoIdx((i) => Math.min(i + 1, stages.length - 1));
    }, stageDurationMs);
    return () => clearInterval(id);
  }, [stages.length, stageDurationMs, currentStage]);

  // Resolve the active stage index from the backend value when present.
  let activeIdx = autoIdx;
  if (currentStage != null) {
    if (typeof currentStage === "number") {
      activeIdx = Math.max(0, Math.min(currentStage, stages.length - 1));
    } else if (stageKeys?.length) {
      // Each stageKeys entry can be a string OR an array of synonymous
      // string keys (used when a stage slot has both a current and legacy
      // backend identifier). Match on either.
      const found = stageKeys.findIndex((entry) =>
        Array.isArray(entry) ? entry.includes(currentStage) : entry === currentStage
      );
      activeIdx = found >= 0 ? found : 0;
    }
  }

  // Rotating live-log lines.
  const [logIdx, setLogIdx] = useState(0);
  useEffect(() => {
    if (logLines.length === 0) return;
    const id = setInterval(() => {
      setLogIdx((i) => (i + 1) % logLines.length);
    }, 800);
    return () => clearInterval(id);
  }, [logLines.length]);

  const visibleLogs = useMemo(() => {
    if (logLines.length === 0) return [];
    // Show 4 lines that drift forward by 1 each tick.
    const out = [];
    for (let k = 0; k < 4; k++) {
      out.push(logLines[(logIdx + k) % logLines.length]);
    }
    return out;
  }, [logIdx, logLines]);

  return (
    <div className="py-8" data-testid="scan-loader">
      {/* Stages checklist */}
      {stages.length > 0 && (
        <ol className="space-y-2.5 mb-6 max-w-md mx-auto">
          {stages.map((s, i) => {
            const done = i < activeIdx;
            const active = i === activeIdx;
            return (
              <li
                key={s}
                className={`flex items-center gap-3 font-mono text-sm transition-colors duration-300 ${
                  done ? "text-emerald-400" : active ? "text-yellow-300" : "text-zinc-600"
                }`}
                data-testid={`scan-stage-${i}`}
                data-state={done ? "done" : active ? "active" : "pending"}
              >
                <span className="w-4 flex justify-center shrink-0">
                  {done && <span className="text-emerald-400">✓</span>}
                  {active && (
                    <span className="w-2 h-2 rounded-full bg-yellow-400 recon-pulse inline-block" />
                  )}
                  {!done && !active && (
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-700 inline-block" />
                  )}
                </span>
                <span className="leading-none">
                  {s}
                  {active && <span className="blink ml-0.5">_</span>}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {/* Indeterminate progress bar */}
      <div className="relative max-w-md mx-auto h-[3px] bg-zinc-900 overflow-hidden rounded-full mb-4">
        <div className="absolute inset-y-0 w-1/3 bg-yellow-400 recon-progress-bar rounded-full" />
      </div>

      {/* Subtitle / ETA */}
      {subtitle && (
        <div className="text-center font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">
          {subtitle}
        </div>
      )}

      {/* Live log strip */}
      {visibleLogs.length > 0 && (
        <div
          className="max-w-md mx-auto border border-zinc-900 bg-black/70 rounded-sm p-3 font-mono text-[11px] leading-relaxed text-zinc-500 space-y-1 overflow-hidden"
          data-testid="scan-log"
        >
          {visibleLogs.map((line, i) => (
            <div
              key={`${logIdx}-${i}`}
              className="recon-log-in truncate"
              style={{
                opacity: 1 - i * 0.22,
                color: i === 0 ? "rgb(250 204 21 / 0.95)" : undefined,
              }}
            >
              <span className="text-zinc-700 mr-2">▸</span>
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
